import { useTranslation } from '../../../i18n/useTranslation';
import styles from './document-list-view.module.scss';
import { useEffect, useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Button, Pagination, Stack, TableContainer, TextField } from '@mui/material';
import classNames from 'classnames';
import globalStyles from '../../../global-styles.module.scss';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { border } from '../../../shared/dropdown/dropdown.service';
import { IPaginatedList } from '../../../shared/models/paginated-list.interface';
import { IDocumentListView } from '../../../shared/models/document-list-view.interface';
import dayjs from 'dayjs';
import DatePickerApp from '../../../shared/date/date-picker';
import { getDocuments } from '../documents.service';
import { htmlId, shouldShowOrderInfoColumns } from './document-list-view.service';
import { IDocumentListFilter } from './interfaces/document-list-filter.interface';
import { DocumentType } from '../../../models/documentType.model';
import { Table, TableHead, TableRow, TableCell, TableBody, Paper, Skeleton } from '@mui/material';
import {
    getExcelOrderSummary,
    getPdfOrderSummary,
    getTotalQuantityShoppingCart,
    reOpenOrderInShoppingCart,
} from '../../../components/shopping-cart/services/shopping-cart.service';
import { DocumentFileContent } from '../../../shared/models/document-file-content.model';
import { getAndShowFileDocument } from '../../../shared/services/attachments.service';
import { pdfFileNameOrderSummary } from '../../shopping-cart-container/order-confirmation/order-confirmation.service';
import { FileDocumentTypeCode } from '../../../shared/models/file-document-type.enum';
import { toast } from 'react-toastify';
import RotatingRing from '../../../components/rotating-ring-animation/RotatingRing';
import { IShopingCart } from '../../../components/order/interfaces/shopping-cart.interface';
import { ErpClaimCode } from '../../../shared/models/erp-claim-code.enum';
import { claimIsActive } from '../../../shared/services/claims.service';
import { useBetween } from 'use-between';
import { PermissionsUseBetween } from '../../../shared/use-between/permissions-use-between';
import ErrorAlert from '../../../shared/alerts/error-alert';
import { getCommonErrorTranslate } from '../../../configuration/axios-configuration/axios.erros.interceptor.service';
import { ShoppingCartUseBetween } from '../../../components/order/use-between/shopping-cart-use-between';
import { useNavigate } from 'react-router-dom';
import { SHOPPING_CART_PATHS } from '../../../shared/constants/breadcrumb.constants';
import OrderDetailsDialog from '../../orders/order-details.dialog';

export interface Props {
    fetchPerUserOnly: boolean;
    documentTypeCodes: string[];
}

const DocumentListView: React.FC<Props> = ({ fetchPerUserOnly, documentTypeCodes }) => {
    const isClientOrderList = documentTypeCodes.includes(DocumentType.ClientOrder);
    const { t } = useTranslation('translation');
    const [page, setPage] = useState<number>(1);
    const [count, setCount] = useState<number>(1);
    const [documents, setDocuments] = useState<IDocumentListView[]>([]);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [documentNumber, setDocumentNumber] = useState<string>('');
    const [clickClearDate, setClickClearDate] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);
    const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
    const [orderDetailsDialogIsOpen, setOpenOrderDetailsDialog] = useState(false);
    const [documentId, setDocumentId] = useState<string>();
    const [documentDate, setDocumentDate] = useState<Date>();
    const [documentNumberDetails, setDocumentNumberDetails] = useState<string>('');
    const [orderBondFileId, setOrderBondFileId] = useState<string | null>();
    const { claims } = useBetween(PermissionsUseBetween);
    const showOrderInfoColumns = shouldShowOrderInfoColumns(isClientOrderList, claims);
    const { setShoppingCart } = useBetween(ShoppingCartUseBetween);
    const { setBuildingSite } = useBetween(ShoppingCartUseBetween);
    const { setBuildingSiteAddress } = useBetween(ShoppingCartUseBetween);
    const { setBuyerContact } = useBetween(ShoppingCartUseBetween);
    const { setIsReadyToOrder } = useBetween(ShoppingCartUseBetween);
    const { setQuantityShoppingCart } = useBetween(ShoppingCartUseBetween);
    const showError = (message: string) => toast.error(message);
    const navigate = useNavigate();
    const [permissionAlertIsOpen, setOpenPermissionAlert] = useState(false);
    const customRed = '#821818';
    const customRedHover = '#a31515';

    const redButtonSx = {
        backgroundColor: customRed,
        color: '#fff',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: customRedHover,
        },
    };

    const getSearchParams = (page: number, isFromClearSearch: boolean = false) => {
        let _startDate: string = startDate;
        let _endDate: string = endDate;
        let _documentNumber: string = documentNumber;
        if (isFromClearSearch) {
            setStartDate((_startDate = ''));
            setEndDate((_endDate = ''));
            setDocumentNumber((_documentNumber = ''));
            setClickClearDate(true);
        }
        return {
            page: page,
            documentNumber: _documentNumber ? _documentNumber : '',
            startDate: _startDate ? _startDate : '',
            endDate: _endDate ? _endDate : '',
            fetchPerUserOnly: fetchPerUserOnly,
            documentTypeCodes: documentTypeCodes,
        } as IDocumentListFilter;
    };

    const getAndSetDocuments = (page: number, isFromClearSearch: boolean = false) => {
        getDocuments(getSearchParams(page, isFromClearSearch)).then((response) => {
            if (response) {
                const dto: IPaginatedList<IDocumentListView> = response.data;
                setDocuments(dto.items);
                setPage(dto.pageIndex + 1);
                setCount(Math.ceil(dto.totalCount));
                if (isFromClearSearch) {
                    setDocumentNumber('');
                }
                setLoading(false);
            }
        });
    };

    const handleChangePage = (page: number) => {
        setLoading(true);
        getAndSetDocuments(page);
    };

    const handleSearch = () => {
        setLoading(true);
        getAndSetDocuments(1);
    };

    const handleClearSearch = () => {
        setLoading(true);
        getAndSetDocuments(1, true);
    };

    const handleChangeSearchValue = (event: any) => {
        setDocumentNumber(event.target.value);
    };

    const handleChangeStartDate = (date: string) => {
        setStartDate(date);
        setClickClearDate(false);
    };

    const handleChangeEndDate = (date: string) => {
        setEndDate(date);
        setClickClearDate(false);
    };

    const getOrderTranslate = (code: string) => {
        return t('FIXCENTER_ORDERING.' + code);
    };

    const handleGetPdfOrderSummary = (documentId: string, documentNumber: string, documentDate: Date) => {
        setIsActionLoading(true);
        getPdfOrderSummary(documentId, documentNumber, documentDate)
            .then((response) => {
                if (response) {
                    const model: DocumentFileContent = response.data;
                    if (!model.message) {
                        getAndShowFileDocument(model.file, pdfFileNameOrderSummary, FileDocumentTypeCode.PDF);
                        setIsActionLoading(false);
                    } else {
                        showError(getOrderTranslate('MESSAGE_9'));
                        setIsActionLoading(false);
                    }
                } else {
                    showError(getOrderTranslate('MESSAGE_9'));
                    setIsActionLoading(false);
                }
            })
            .catch(() => {
                showError(getOrderTranslate('MESSAGE_9'));
                setIsActionLoading(false);
            });
    };

    const handleGetExcelOrderSummary = (documentId: string, documentNumber: string, documentDate: Date) => {
        setIsActionLoading(true);
        getExcelOrderSummary(documentId, documentNumber, documentDate)
            .then((response) => {
                if (response) {
                    const model: DocumentFileContent = response.data;
                    if (!model.message) {
                        getAndShowFileDocument(model.file, documentNumber, FileDocumentTypeCode.EXCEL, false);
                        setIsActionLoading(false);
                    } else {
                        showError(getOrderTranslate('MESSAGE_9'));
                        setIsActionLoading(false);
                    }
                } else {
                    showError(getOrderTranslate('MESSAGE_9'));
                    setIsActionLoading(false);
                }
            })
            .catch(() => {
                showError(getOrderTranslate('MESSAGE_9'));
                setIsActionLoading(false);
            });
    };

    const openOrderDetailsDialog = (item: IDocumentListView) => {
        setDocumentId(item.id);
        setDocumentNumberDetails(item.documentNumber);
        setDocumentDate(item.documentDate);
        setOrderBondFileId(item.orderBondFileId);
        setOpenOrderDetailsDialog(true);
    };

    const handleReOpenOrderInShoppingCart = (documentId: string) => {
        if (!claimIsActive(claims, ErpClaimCode.CUSTOMER_ECOM_ROLE)) {
            setOpenPermissionAlert(true);
        } else {
            setIsActionLoading(true);
            reOpenOrderInShoppingCart(documentId).then((response) => {
                if (response) {
                    const data: IShopingCart = response.data;
                    setShoppingCart(data);
                    setBuildingSite(data.buildingSite);
                    // buildingSiteAddress/buyerContact are separate shared state (see
                    // ShoppingCartUseBetween) that the delivery address/contact selects read from - without
                    // refreshing them here too, a reopened order's backfilled address/contact (see
                    // ShoppingCartService.ReOpenOrderInShoppingCart) only shows up after a full page reload.
                    setBuildingSiteAddress(data.buildingSiteAddress);
                    setBuyerContact(data.buyerContact);
                    setIsReadyToOrder(data.isReadyToOrder);
                    setQuantityShoppingCart(getTotalQuantityShoppingCart(data));
                    navigate(SHOPPING_CART_PATHS[1].link);
                    setIsActionLoading(false);
                }
            });
        }
    };

    useEffect(() => {
        getAndSetDocuments(page);
    }, []);

    return (
        <Box
            sx={{
                px: 3,
                py: 2,
            }}
        >
            {isActionLoading && <RotatingRing />}
            <ErrorAlert
                isOpen={permissionAlertIsOpen}
                handleCloseDialog={(val: boolean) => setOpenPermissionAlert(val)}
                translateCode={getCommonErrorTranslate(ErpClaimCode[ErpClaimCode.CUSTOMER_ECOM_ROLE])}
            ></ErrorAlert>
            <OrderDetailsDialog
                isOpen={orderDetailsDialogIsOpen}
                handleCloseDialog={(isOpen: boolean) => setOpenOrderDetailsDialog(isOpen)}
                documentId={documentId as string}
                documentNumber={documentNumberDetails as string}
                documentDate={documentDate as Date}
                orderBondFileId={orderBondFileId as string | null}
            ></OrderDetailsDialog>
            <DialogTitle sx={{ px: 0 }}>
                <div>
                    <label>
                        {documentTypeCodes.includes(DocumentType.InvoiceProforma) && t('INVOICES_ALL')}
                        {isClientOrderList && t('ORDERS_ALL')}
                    </label>
                </div>
            </DialogTitle>
            <section className={classNames(styles.search_content, globalStyles.float_left_item)}>
                <div>
                    <label className={classNames(globalStyles.float_left_item, styles.field_label)}>
                        {t('NUMBER')}
                    </label>
                    <TextField
                        className={globalStyles.mr_3}
                        size="small"
                        value={documentNumber}
                        onChange={handleChangeSearchValue}
                        inputProps={{ style: { fontSize: 14 } }}
                        InputLabelProps={{
                            sx: { color: 'grey', '&.Mui-focused': { color: 'grey' } },
                        }}
                        sx={{
                            width: '300px',
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    border: { border },
                                },
                                '&.Mui-focused fieldset': {
                                    border: { border },
                                    color: 'red',
                                },
                                '&:hover fieldset': {
                                    border: { border },
                                },
                                '& .css-1jy569b-MuiFormLabel-root-MuiInputLabel-root.Mui-focused': {
                                    color: 'red',
                                },
                            },
                        }}
                    ></TextField>
                </div>
                <div className={globalStyles.mr_3}>
                    <label className={classNames(globalStyles.float_left_item, styles.field_label)}>
                        {t('START_DATE')}
                    </label>
                    <DatePickerApp handleChange={handleChangeStartDate} clear={clickClearDate} />
                </div>
                <div className={globalStyles.mr_3}>
                    <label className={classNames(globalStyles.float_left_item, styles.field_label)}>
                        {t('END_DATE')}
                    </label>
                    <DatePickerApp handleChange={handleChangeEndDate} clear={clickClearDate} />
                </div>
                <button
                    id={`${htmlId}-search-btn`}
                    onClick={() => {
                        handleSearch();
                    }}
                    className={classNames(globalStyles.defult_btn, globalStyles.mr_1, styles.btn)}
                >
                    <SearchIcon className={globalStyles.mr_1}></SearchIcon>
                    {t('SEARCH')}
                </button>
                <button
                    id={`${htmlId}-clear-search-btn`}
                    onClick={() => {
                        handleClearSearch();
                    }}
                    className={classNames(globalStyles.defult_btn, styles.btn)}
                >
                    <ClearIcon className={globalStyles.mr_1}></ClearIcon>
                    {t('CLEAR_SEARCH')}
                </button>
            </section>
            <Paper
                elevation={0}
                sx={{
                    mt: 2,
                    height: '50vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <TableContainer
                    sx={{
                        maxHeight: '50vh',
                        overflow: 'auto',
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                    {t('NUMBER')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>{t('DATE')}</TableCell>
                                {!isClientOrderList && (
                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                        {t('STATUS')}
                                    </TableCell>
                                )}
                                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                    {t('ISSUER')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                    {t('CLIENT')}
                                </TableCell>
                                {showOrderInfoColumns && (
                                    <>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                            {t('BUYER_CONTACT')}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                            {t('ORDER_METHOD')}
                                        </TableCell>
                                    </>
                                )}
                                {isClientOrderList && <TableCell sx={{ backgroundColor: '#f5f5f5' }} />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ? [...Array(5)].map((_, index) => (
                                      <TableRow key={index}>
                                          <TableCell>
                                              <Skeleton variant="text" />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton variant="text" />
                                          </TableCell>
                                          {!isClientOrderList && (
                                              <TableCell>
                                                  <Skeleton variant="text" />
                                              </TableCell>
                                          )}
                                          <TableCell>
                                              <Skeleton variant="text" />
                                          </TableCell>
                                          <TableCell>
                                              <Skeleton variant="text" />
                                          </TableCell>
                                          {showOrderInfoColumns && (
                                              <>
                                                  <TableCell>
                                                      <Skeleton variant="text" />
                                                  </TableCell>
                                                  <TableCell>
                                                      <Skeleton variant="text" />
                                                  </TableCell>
                                              </>
                                          )}
                                          {isClientOrderList && (
                                              <TableCell>
                                                  <Skeleton variant="text" />
                                              </TableCell>
                                          )}
                                      </TableRow>
                                  ))
                                : documents &&
                                  documents.map((item, index) => (
                                      <TableRow
                                          key={item.id}
                                          sx={{
                                              backgroundColor: index % 2 !== 0 ? '#fafafa' : 'inherit',
                                          }}
                                      >
                                          <TableCell>{item.documentNumber}</TableCell>
                                          <TableCell>{dayjs(item.documentDate).format('DD/MM/YYYY')}</TableCell>
                                          {!isClientOrderList && (
                                              <TableCell>{t('documentStatus.' + item.documentStatusCode)}</TableCell>
                                          )}
                                          <TableCell>{item.firstContractorName}</TableCell>
                                          <TableCell>{item.secondContractorName}</TableCell>
                                          {showOrderInfoColumns && (
                                              <>
                                                  <TableCell>{item.buyerContact}</TableCell>
                                                  <TableCell>{item.orderMethod}</TableCell>
                                              </>
                                          )}

                                          {isClientOrderList && (
                                              <TableCell>
                                                  <Stack direction="row" spacing={1}>
                                                      <Button
                                                          size="small"
                                                          variant="contained"
                                                          sx={redButtonSx}
                                                          onClick={() => {
                                                              handleGetPdfOrderSummary(
                                                                  item.id,
                                                                  item.documentNumber,
                                                                  item.documentDate
                                                              );
                                                          }}
                                                      >
                                                          {t('Pdf')}
                                                      </Button>
                                                      <Button
                                                          size="small"
                                                          variant="contained"
                                                          sx={redButtonSx}
                                                          onClick={() => {
                                                              handleGetExcelOrderSummary(
                                                                  item.id,
                                                                  item.documentNumber,
                                                                  item.documentDate
                                                              );
                                                          }}
                                                      >
                                                          {t('Excel')}
                                                      </Button>
                                                      <Button
                                                          size="small"
                                                          variant="contained"
                                                          sx={redButtonSx}
                                                          onClick={() => {
                                                              openOrderDetailsDialog(item);
                                                          }}
                                                      >
                                                          {t('DETAILS')}
                                                      </Button>
                                                      <Button
                                                          size="small"
                                                          variant="contained"
                                                          sx={redButtonSx}
                                                          onClick={() => {
                                                              handleReOpenOrderInShoppingCart(item.id);
                                                          }}
                                                      >
                                                          {t('RE_ORDER')}
                                                      </Button>
                                                  </Stack>
                                              </TableCell>
                                          )}
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            <Stack alignItems="center" sx={{ mt: 2 }}>
                <Pagination
                    count={count}
                    boundaryCount={1}
                    page={page}
                    onChange={(_, page) => handleChangePage(page)}
                />
            </Stack>
        </Box>
    );
};
export default DocumentListView;
